import { lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import {
  FiArrowLeft, FiArrowRight, FiArrowUpRight, FiCalendar, FiCheck, FiChevronLeft, FiClock, FiCopy,
  FiChevronDown, FiGrid, FiHeart, FiHome, FiMapPin, FiMaximize2, FiMessageCircle,
  FiAlertTriangle, FiShare2, FiUser, FiX,
} from 'react-icons/fi';
import {
  Accessibility, BellRing, Building2, CarFront, CookingPot, Droplets, Dumbbell,
  Fence, Flame, Heater, LockKeyhole, PawPrint, ShieldCheck, ShowerHead, Snowflake,
  Sparkles, Sun, Trees, Utensils, WashingMachine, Waves, Wifi, Wind, Zap,
} from 'lucide-react';
import { getPropertyById } from '../services/propertiesService';
import BarrioInfo from '../components/EstiloDeVidaInfo/BarrioInfo';
import { apiUrl } from '../config/api';
import { acquisitionSource, trackProductEvent } from '../services/productAnalytics';
import DeferredRender from '../components/common/DeferredRender';
import ReviewsSection from '../components/ReviewsSection';
import { useAuth } from '../auth/AuthContext';
import PageSeo from '../components/PageSeo';
import { getCurrentUserHiddenKey, isPropertyHidden, toggleHiddenProperty } from '../utils/hiddenProperties';

const PropertyMap=lazy(()=>import('../components/map/PropertyMap'));
const GoogleStreetView=lazy(()=>import('../components/map/GoogleStreetView'));
const NearbyPlaces=lazy(()=>import('../components/map/NearbyPlaces'));
const VacationNearbyHighlights=lazy(()=>import('../components/map/VacationNearbyHighlights'));

const FALLBACK_IMAGE =
  'data:image/svg+xml;charset=UTF-8,%3Csvg xmlns="http://www.w3.org/2000/svg" width="1400" height="900" viewBox="0 0 1400 900"%3E%3Crect width="1400" height="900" fill="%23e7e3dc"/%3E%3Cpath d="M555 520V385l145-115 145 115v135H555Z" fill="none" stroke="%237b898c" stroke-width="9"/%3E%3Cpath d="M640 520V415h120v105" fill="none" stroke="%237b898c" stroke-width="9"/%3E%3C/svg%3E';

const humanize = (value) => String(value || '').replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());

const FEATURE_LABELS = {
  wifi: 'Wi-Fi', ropa_cama: 'Ropa de cama', ropa_blanca: 'Ropa blanca', cocina_equipada: 'Cocina equipada',
  recepcion_24h: 'Recepción 24 h', romantica: 'Escapada romántica', familias: 'Ideal para familias',
  aventura: 'Aventura y montaña', vino_gastronomia: 'Vino y gastronomía', gastronomia: 'Gastronomía',
  bienestar: 'Relax y bienestar', trabajo_remoto: 'Trabajo remoto', montana: 'Montaña',
  rios_diques: 'Ríos o diques', nieve: 'Nieve y esquí', senderos: 'Senderismo', vista_abierta: 'Vista abierta',
};
const featureLabel = (value) => FEATURE_LABELS[value] || humanize(value);
const normalizedFeature = (value) => String(value || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/_/g, ' ').trim();
const uniqueStrings = (values) => [...new Map(
  values
    .filter((value) => typeof value === 'string' && value.trim())
    .map((value) => [normalizedFeature(value), value])
).values()];
const isRepeatedVacationFact = (value) => /(^|\s)(\d+\s*)?(dormitorios?|habitaciones?|banos?|huespedes?|camas?|metros(\s+(totales|cubiertos))?)(\s|$)/i.test(normalizedFeature(value));
const SPECIAL_FEATURES = new Set(['terraza', 'piscina', 'pileta', 'vista abierta', 'vista a la montana', 'jardin', 'balcon']);
const featureIcon = (value) => {
  const clean = normalizedFeature(value);
  if (clean.includes('wifi') || clean.includes('internet')) return <Wifi />;
  if (clean.includes('pileta') || clean.includes('piscina')) return <Waves />;
  if (clean.includes('jardin') || clean.includes('parque') || clean.includes('arboleda')) return <Trees />;
  if (clean.includes('seguridad') || clean.includes('vigilancia') || clean.includes('alarma')) return <ShieldCheck />;
  if (clean === 'sum' || clean.includes('salon de usos multiples')) return <Building2 />;
  if (clean.includes('agua') || clean.includes('riego') || clean.includes('cloaca')) return <Droplets />;
  if (clean.includes('calefaccion') || clean.includes('caldera') || clean.includes('radiador')) return <Heater />;
  if (clean.includes('aire acondicionado') || clean.includes('climatizacion')) return <Snowflake />;
  if (clean.includes('estacionamiento') || clean.includes('cochera') || clean.includes('garage')) return <CarFront />;
  if (clean.includes('parrilla') || clean.includes('churrasquera') || clean.includes('fog')) return <Flame />;
  if (clean.includes('quincho') || clean.includes('galeria')) return <Fence />;
  if (clean.includes('cocina')) return <CookingPot />;
  if (clean.includes('comedor') || clean.includes('desayuno')) return <Utensils />;
  if (clean.includes('gimnasio') || clean.includes('fitness')) return <Dumbbell />;
  if (clean.includes('mascota')) return <PawPrint />;
  if (clean.includes('lavanderia') || clean.includes('lavarropas')) return <WashingMachine />;
  if (clean.includes('ducha') || clean.includes('bano')) return <ShowerHead />;
  if (clean.includes('terraza') || clean.includes('balcon') || clean.includes('solarium') || clean.includes('luminos')) return <Sun />;
  if (clean.includes('vista') || clean.includes('ventilacion')) return <Wind />;
  if (clean.includes('accesib')) return <Accessibility />;
  if (clean.includes('portero') || clean.includes('timbre')) return <BellRing />;
  if (clean.includes('cerradura') || clean.includes('acceso')) return <LockKeyhole />;
  if (clean.includes('electric')) return <Zap />;
  return <Sparkles />;
};

export function PropertyDetail({ previewProperty = null, previewMode = false }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [property, setProperty] = useState(previewProperty);
  const [loading, setLoading] = useState(!previewProperty);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);
  const userHiddenKey = getCurrentUserHiddenKey(user);
  const hidden = property?._id ? isPropertyHidden(property._id, userHiddenKey) : false;
  const [isOwner, setIsOwner] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [notice, setNotice] = useState('');
  const [demoContactOpen, setDemoContactOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState('FRAUD');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const [reportError, setReportError] = useState('');
  const [streetViewAvailable, setStreetViewAvailable] = useState(null);
  const [streetViewOpen, setStreetViewOpen] = useState(() => window.matchMedia('(min-width: 701px)').matches);
  const mobileGalleryRef = useRef(null);
  const editorialCampaignId = searchParams.get('campaign');
  const trackEditorialContact = () => {
    if (!editorialCampaignId) return;
    fetch(apiUrl(`/editorial/campaigns/${encodeURIComponent(editorialCampaignId)}/events`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event: 'contact' }),
      keepalive: true,
    }).catch(() => {});
  };

  useEffect(() => {
    if (previewProperty) {
      setProperty(previewProperty);
      setLoading(false);
      setError('');
      setCurrentIndex(0);
      return undefined;
    }
    let active = true;
    setLoading(true);
    setCurrentIndex(0);
    getPropertyById(id, { preferCache:true })
      .then(async (propertyData) => {
        if (!active) return;
        setProperty(propertyData);
        trackProductEvent('PROPERTY_VIEW',{property:propertyData._id,source:acquisitionSource(),metadata:{operation:propertyData.operation,type:propertyData.type}});
        try {
          const response = await fetch(apiUrl('/users/me'), { credentials: 'include' });
          if (!response.ok) return;
          const user = await response.json();
          if (!active) return;
          setSaved((user.favorites || []).some((favorite) => String(favorite?._id || favorite) === String(propertyData._id)));
          setIsOwner(String(propertyData.author?._id || propertyData.author) === String(user._id));
        } catch { /* La ficha sigue disponible aunque falle el perfil. */ }
      })
      .catch(() => active && setError('No pudimos abrir esta propiedad. Es posible que ya no esté disponible.'))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [id, previewProperty]);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = window.setTimeout(() => setNotice(''), 2600);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    if (previewMode) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && demoContactOpen) {
        setDemoContactOpen(false);
        return;
      }
      if (event.key === 'Escape' && reportOpen) {
        setReportOpen(false);
        return;
      }
      if (event.key === 'Escape') navigate(-1);
      const total = (property?.images?.filter(Boolean).length || 1) + (property?.video?.url ? 1 : 0);
      if (event.key === 'ArrowRight') setCurrentIndex((value) => (value + 1) % total);
      if (event.key === 'ArrowLeft') setCurrentIndex((value) => value === 0 ? total - 1 : value - 1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [navigate, property, reportOpen, demoContactOpen, previewMode]);

  useEffect(() => {
    if (previewMode) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [previewMode]);

  const images = useMemo(() => {
    const validImages = (property?.images || []).map((image) => typeof image === 'string' ? image : image?.preview || image?.url).filter(Boolean);
    return validImages.length ? validImages : [FALLBACK_IMAGE];
  }, [property]);
  const galleryItems = useMemo(() => {
    const items = [{ type:'image', src:images[0] }];
    if (property?.video?.url) {
      items.push({
        type:'video',
        src:property.video.url,
        poster:property.video.poster || images[0],
      });
    }
    images.slice(1).forEach((src) => items.push({ type:'image', src }));
    return items;
  }, [images, property]);

  const featureGroups = useMemo(() => {
    const regular = property?.features && !Array.isArray(property.features) ? Object.values(property.features).flat() : [];
    const services = property?.vacation?.services || [];
    const experiences = property?.vacation?.experiences || [];
    const nearby = property?.vacation?.nearby || [];
    const contextualValues = new Set([...services, ...experiences, ...nearby]);
    const cleanRegular = regular.filter((value) => !isRepeatedVacationFact(value) && !contextualValues.has(value));
    const special = uniqueStrings(cleanRegular.filter((value) => SPECIAL_FEATURES.has(normalizedFeature(value))));
    const accommodation = uniqueStrings([
      ...cleanRegular.filter((value) => !SPECIAL_FEATURES.has(normalizedFeature(value))),
      ...services,
      ...(property?.garage ? ['estacionamiento'] : []),
    ]);
    return {
      all: uniqueStrings([
        ...cleanRegular,
        ...services.filter((value) => !isRepeatedVacationFact(value)),
        ...experiences.filter((value) => !isRepeatedVacationFact(value)),
        ...nearby.filter((value) => !isRepeatedVacationFact(value)),
        ...(property?.rental?.includedServices || []).filter((value) => !isRepeatedVacationFact(value)),
      ]),
      special,
      accommodation,
      experiences: uniqueStrings(experiences),
      nearby: uniqueStrings(nearby),
    };
  }, [property]);

  if (hidden) {
    return (
      <PageState>
        <strong>Esta propiedad quedó descartada para vos.</strong>
        <p>La ocultamos para que no vuelva a aparecer en tus resultados.</p>
        <StateButton onClick={() => navigate('/buscar')}>Volver a ver propiedades</StateButton>
        <SecondaryButton type="button" onClick={() => { toggleHiddenProperty(property._id, userHiddenKey); navigate('/buscar'); }}>
          Mostrarla de nuevo
        </SecondaryButton>
      </PageState>
    );
  }

  if (loading) return <PageState><Loader /><strong>Preparando la propiedad…</strong></PageState>;
  if (error || !property) return <PageState><strong>{error}</strong><StateButton onClick={() => navigate(-1)}>Volver</StateButton></PageState>;

  const formattedPrice = new Intl.NumberFormat('es-AR', {
    style: 'currency', currency: property.currency || 'USD', maximumFractionDigits: 0,
  }).format(Number(property.price) || 0);
  const locationText = [property.neighborhood, property.department, property.province?.replace(/_/g, ' ')].filter(Boolean).join(', ') || 'Ubicación a confirmar';
  const referenceSurface = Number(property.surfaceCovered || property.surfaceTotal || 0);
  const pricePerSquareMeter = property.operation === 'alquiler_vacacional'
    ? 0
    : Number(property.pricePerSquareMeter || (referenceSurface > 0 ? property.price / referenceSurface : 0));
  const formattedSquareMeter = pricePerSquareMeter > 0 ? new Intl.NumberFormat('es-AR', {
    style: 'currency', currency: property.currency || 'USD', maximumFractionDigits: 0,
  }).format(pricePerSquareMeter) : '';
  const formatMarketValue = (value) => new Intl.NumberFormat('es-AR', {
    style: 'currency', currency: property.currency || 'USD', maximumFractionDigits: 0,
  }).format(Number(value) || 0);
  const whatsappNumber = property.author?.role === 'PROFESSIONAL'
    ? property.author?.professionalData?.phone || property.author?.phone
    : property.author?.phone;
  const canUseWhatsapp = Boolean(
    property.author?.allowWhatsapp
    && whatsappNumber
  );
  const propertyReference = property.reference || `NIDO-${String(property._id).slice(-7).toUpperCase()}`;
  const stayRequest = property.operation === 'alquiler_vacacional' ? {
    checkIn: searchParams.get('checkIn') || '',
    checkOut: searchParams.get('checkOut') || '',
    guests: Math.max(1, Number(searchParams.get('guests')) || 1),
  } : null;
  const hasStayDates = Boolean(stayRequest?.checkIn && stayRequest?.checkOut);
  const formatStayDate = (value) => value
    ? new Date(`${value}T12:00:00`).toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  const stayMessage = hasStayDates
    ? ` para el ${formatStayDate(stayRequest.checkIn)} al ${formatStayDate(stayRequest.checkOut)}, para ${stayRequest.guests} ${stayRequest.guests === 1 ? 'huésped' : 'huéspedes'}`
    : '';

  const seoDescription = String(property.description || `${humanize(property.type)} en ${locationText}`)
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 155);
  const seoTitle = `${property.title} en ${locationText} | NIDO`;
  const seoImage = images.find((image) => /^https?:\/\//i.test(image));
  const propertySchema = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: property.title,
    description: seoDescription,
    url: `https://www.nidoportal.com/properties/${property._id}`,
    ...(seoImage ? { image: images.filter((image) => /^https?:\/\//i.test(image)).slice(0, 10) } : {}),
    datePosted: property.createdAt,
    ...(property.updatedAt ? { dateModified: property.updatedAt } : {}),
    offers: {
      '@type': 'Offer',
      price: Number(property.price) || 0,
      priceCurrency: property.currency || 'USD',
      availability: 'https://schema.org/InStock',
    },
    about: {
      '@type': 'Accommodation',
      name: property.title,
      ...(property.rooms ? { numberOfRooms: Number(property.rooms) } : {}),
      ...(property.bedrooms ? { numberOfBedrooms: Number(property.bedrooms) } : {}),
      ...(referenceSurface ? { floorSize: { '@type': 'QuantitativeValue', value: referenceSurface, unitCode: 'MTK' } } : {}),
      address: {
        '@type': 'PostalAddress',
        addressLocality: property.neighborhood || property.department,
        addressRegion: property.province || undefined,
        addressCountry: 'AR',
      },
    },
  };

  const previousImage = () => setCurrentIndex((value) => value === 0 ? galleryItems.length - 1 : value - 1);
  const nextImage = () => setCurrentIndex((value) => (value + 1) % galleryItems.length);
  const handleMobileGalleryScroll = (event) => {
    const width = event.currentTarget.clientWidth;
    if (!width) return;
    const nextIndex = Math.round(event.currentTarget.scrollLeft / width);
    if (nextIndex !== currentIndex) setCurrentIndex(nextIndex);
  };

  const handleShare = async () => {
    try {
      if (navigator.share) await navigator.share({ title: property.title, text: property.description, url: window.location.href });
      else {
        await navigator.clipboard.writeText(window.location.href);
        setNotice('Enlace copiado');
      }
    } catch (shareError) {
      if (shareError.name !== 'AbortError') setNotice('No pudimos compartir el enlace');
    }
  };

  const handleDismissProperty = () => {
    toggleHiddenProperty(property._id, userHiddenKey);
    window.dispatchEvent(new CustomEvent('nido:hidden-property-updated', { detail: { propertyId: String(property._id), userId: userHiddenKey } }));
    navigate('/buscar');
  };

  const handleToggleFavorite = async () => {
    if (!user) return navigate('/login', { state:{ from:{ pathname:`/properties/${property._id}` } } });
    try {
      const response = await fetch(apiUrl(`/users/favorites/${property._id}`), {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (response.ok) {
        setSaved((data.favorites || []).some((favorite) => String(favorite?._id || favorite) === String(property._id)));
        setNotice(saved ? 'Quitada de favoritos' : 'Guardada en favoritos');
        trackProductEvent(saved?'FAVORITE_REMOVED':'FAVORITE_ADDED',{property:property._id});
      }
    } catch { setNotice('No pudimos actualizar tus favoritos'); }
  };

  const handleContact = (visit = false) => {
    if (property.isDemo) {
      setDemoContactOpen(true);
      return;
    }
    trackProductEvent('CONTACT_STARTED',{property:property._id,metadata:{intent:visit?'visit':'question'}});
    trackEditorialContact();
    const defaultMessage = visit
      ? `Hola, estoy interesado en esta propiedad. Referencia ${propertyReference}. Me gustaría coordinar una visita.`
      : property.operation === 'alquiler_vacacional'
        ? `Hola, quisiera consultar la disponibilidad de este alojamiento${stayMessage}. Referencia ${propertyReference}.`
        : `Hola, estoy interesado en esta propiedad. Referencia ${propertyReference}.`;
    const destination = {
      pathname: '/mensajes',
      state: {
        propertyId: property._id,
        intent: visit ? 'visit' : 'question',
        defaultMessage,
        source:acquisitionSource(),
        ...(property.operation === 'alquiler_vacacional' ? { vacationRequest: stayRequest } : {}),
      },
    };
    if (!user) {
      navigate('/login', { state: { from: destination } });
      return;
    }
    navigate('/mensajes', { state: destination.state });
  };

  const handleWhatsApp = () => {
    if (property.isDemo) {
      setDemoContactOpen(true);
      return;
    }
    if (!canUseWhatsapp) return;
    let normalizedNumber = String(whatsappNumber).replace(/\D/g, '');
    if (normalizedNumber.startsWith('0')) normalizedNumber = normalizedNumber.slice(1);
    normalizedNumber = normalizedNumber.replace(/^(261|264|266|260|263)15/, '$1');
    if (normalizedNumber.startsWith('54') && !normalizedNumber.startsWith('549')) {
      normalizedNumber = `549${normalizedNumber.slice(2)}`;
    } else if (!normalizedNumber.startsWith('54') && normalizedNumber.length === 10) {
      normalizedNumber = `549${normalizedNumber}`;
    }
    if (normalizedNumber.length < 11 || normalizedNumber.length > 15) {
      setNotice('El anunciante debe revisar su número de WhatsApp');
      return;
    }
    const propertyUrl = `${window.location.origin}/properties/${property._id}`;
    const message = property.operation === 'alquiler_vacacional'
      ? `Hola, quisiera consultar la disponibilidad de "${property.title}"${stayMessage}. Referencia ${propertyReference}.\n\n${propertyUrl}`
      : `Hola, estoy interesado en la propiedad "${property.title}". Referencia ${propertyReference}. ¿Sigue disponible?\n\n${propertyUrl}`;
    trackProductEvent('CONTACT_STARTED',{property:property._id,metadata:{intent:'whatsapp'}});
    trackEditorialContact();
    window.open(`https://wa.me/${normalizedNumber}?text=${encodeURIComponent(message)}`, '_blank', 'noopener,noreferrer');
  };

  const openReport = () => {
    if (!user) {
      navigate('/login', { state: { from: { pathname: `/properties/${property._id}` } } });
      return;
    }
    setReportError('');
    setReportOpen(true);
  };

  const submitReport = async (event) => {
    event.preventDefault();
    setReportSubmitting(true);
    setReportError('');
    try {
      const response = await fetch(apiUrl(`/properties/${property._id}/report`), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: reportReason, details: reportDetails }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'No pudimos registrar la denuncia');
      setReportOpen(false);
      setReportDetails('');
      setNotice('Gracias. Recibimos tu denuncia.');
    } catch (submitError) {
      setReportError(submitError.message);
    } finally {
      setReportSubmitting(false);
    }
  };

  const details = [
    ['Tipo', humanize(property.type), <FiHome />],
    ['Operación', humanize(property.operation), <FiGrid />],
    ['Ambientes', property.operation === 'alquiler_vacacional' ? null : property.rooms, <FiHome />],
    ['Dormitorios', property.bedrooms, <FiHome />],
    ['Baños', property.bathrooms, <FiCheck />],
    ['Superficie total', property.surfaceTotal ? `${property.surfaceTotal} m²` : null, <FiMaximize2 />],
    ['Superficie cubierta', property.surfaceCovered ? `${property.surfaceCovered} m²` : null, <FiMaximize2 />],
    ['Antigüedad', property.age !== undefined && property.age !== null ? `${property.age} años` : null, <FiClock />],
    ['Huéspedes', property.vacation?.maximumGuests ? `Hasta ${property.vacation.maximumGuests}` : null, <FiHome />],
    ['Camas', property.vacation?.beds ? `${property.vacation.beds} · ${property.vacation.sleepingPlaces || property.vacation.maximumGuests} plazas` : null, <FiHome />],
    ['Estadía mínima', property.vacation?.minimumNights ? `${property.vacation.minimumNights} noches` : null, <FiCalendar />],
    ['Check-in', property.vacation?.checkIn ? `Desde ${property.vacation.checkIn}` : null, <FiClock />],
    ['Check-out', property.vacation?.checkOut ? `Hasta ${property.vacation.checkOut}` : null, <FiClock />],
    ['Estado', humanize(property.condition), <FiCheck />],
    ['Orientación', humanize(property.orientation), <FiMapPin />],
    ['Garage', property.garage ? 'Sí' : null, <FiCheck />],
    ['Expensas', property.expenses ? new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(property.expenses) : null, <FiCopy />],
    ['Contrato mínimo', property.rental?.contractMonths ? `${property.rental.contractMonths} meses` : null, <FiCalendar />],
    ['Depósito', property.rental?.depositMonths !== undefined ? `${property.rental.depositMonths} meses` : null, <FiCopy />],
    ['Amoblada', property.rental?.furnished ? 'Sí' : null, <FiCheck />],
  ].filter(([, value]) => value !== null && value !== undefined && value !== '');
  const unavailable = property.status && property.status !== 'disponible';
  const statusLabel = ({ reservado:'reservada', pausado:'pausada', vendido:'vendida', alquilado:'alquilada' })[property.status] || property.status;
  const DetailShell = previewMode ? PreviewShell : Overlay;
  const DetailPanel = previewMode ? PreviewPanel : Panel;

  return (
    <>
    <PageSeo
      title={seoTitle}
      description={seoDescription}
      path={`/properties/${property._id}`}
      image={seoImage}
      type="product"
      robots={unavailable || property.isDemo ? 'noindex,follow' : undefined}
      structuredData={propertySchema}
    />
    <DetailShell onMouseDown={previewMode ? undefined : (event) => event.target === event.currentTarget && navigate(-1)}>
      <DetailPanel>
        <TopBar>
          <BackButton type="button" onClick={() => navigate(-1)}><FiChevronLeft /> Volver</BackButton>
          <TopIdentity><span>NIDO</span><i />{humanize(property.type)} en {locationText}</TopIdentity>
          <TopActions>
            <CircleButton type="button" onClick={handleShare} aria-label="Compartir"><FiShare2 /></CircleButton>
            <CircleButton type="button" onClick={handleToggleFavorite} $active={saved} aria-label="Guardar en favoritos"><FiHeart fill={saved ? 'currentColor' : 'none'} /></CircleButton>
            <CloseButton type="button" onClick={() => navigate(-1)} aria-label="Cerrar"><FiX /></CloseButton>
          </TopActions>
        </TopBar>

        {unavailable && <AvailabilityBanner role="status"><div><strong>Esta propiedad está {statusLabel}.</strong><span>Ya no aparece en los resultados de búsqueda. Podés conservarla en favoritos o explorar alternativas similares.</span></div><Link to={`/buscar?operation=${property.operation||''}&type=${property.type||''}&department=${property.department||''}`}>Ver propiedades similares <FiArrowRight/></Link></AvailabilityBanner>}

        <Gallery>
          <DesktopGalleryMedia>
          {galleryItems[currentIndex]?.type === 'video' ? (
            <GalleryVideo
              src={galleryItems[currentIndex].src}
              poster={galleryItems[currentIndex].poster}
              autoPlay
              muted
              loop
              controls
              playsInline
              preload="metadata"
              aria-label={`Video de ${property.title}`}
            />
          ) : (
            <MainImage src={galleryItems[currentIndex]?.src || FALLBACK_IMAGE} alt={`${property.title} — foto ${currentIndex + 1}`} onError={(event) => { event.currentTarget.src = FALLBACK_IMAGE; }} />
          )}
          </DesktopGalleryMedia>
          <MobileGalleryTrack ref={mobileGalleryRef} onScroll={handleMobileGalleryScroll}>
            {galleryItems.map((item, index) => (
              <MobileGallerySlide key={`${item.src}-${index}`}>
                {item.type === 'video' ? (
                  <GalleryVideo
                    src={item.src}
                    poster={item.poster}
                    autoPlay
                    muted
                    loop
                    controls
                    playsInline
                    preload="metadata"
                    aria-label={`Video de ${property.title}`}
                  />
                ) : (
                  <MainImage src={item.src || FALLBACK_IMAGE} alt={`${property.title} — foto ${index + 1}`} onError={(event) => { event.currentTarget.src = FALLBACK_IMAGE; }} />
                )}
              </MobileGallerySlide>
            ))}
          </MobileGalleryTrack>
          <GalleryOverlay />
          {galleryItems.length > 1 && (
            <>
              <GalleryArrow type="button" $left onClick={previousImage} aria-label="Elemento anterior"><FiArrowLeft /></GalleryArrow>
              <GalleryArrow type="button" onClick={nextImage} aria-label="Elemento siguiente"><FiArrowRight /></GalleryArrow>
            </>
          )}
          <GalleryMeta>
            {property.isDemo && <Featured>DEMO NIDO</Featured>}
            {galleryItems[currentIndex]?.type === 'video' && <Featured>VIDEO</Featured>}
            <PhotoCount>{currentIndex + 1} / {galleryItems.length}</PhotoCount>
          </GalleryMeta>
          {galleryItems.length > 1 && (
            <Thumbnails>
              {galleryItems.slice(0, 7).map((item, index) => (
                <Thumbnail type="button" key={`${item.src}-${index}`} $active={currentIndex === index} onClick={() => setCurrentIndex(index)}>
                  <img src={item.poster || item.src} alt={item.type === 'video' ? 'Ver video' : `Ver foto ${index + 1}`} />
                  {item.type === 'video' && <span>VIDEO</span>}
                </Thumbnail>
              ))}
            </Thumbnails>
          )}
        </Gallery>

        <Content className={previewMode ? 'preview-content' : undefined}>
          <PropertyHero>
            <HeroMain>
              <Meta>
                {humanize(property.operation)} · {humanize(property.type)} · {humanize(property.status || 'disponible')}
                {property.isPromoted && !property.isDemo && <PromotionDisclosure>Publicación promocionada</PromotionDisclosure>}
              </Meta>
              <Title>{property.title}</Title>
              <TitleActions>
                <HideInlineButton type="button" onClick={handleDismissProperty} aria-label="No me interesa esta propiedad">
                  <FiX />
                  <span>No me interesa</span>
                </HideInlineButton>
              </TitleActions>
              <Address><FiMapPin /> <span>{property.address || locationText}</span></Address>
            </HeroMain>
            <PriceBlock><PriceLabel>{property.operation === 'alquiler_vacacional' ? 'POR NOCHE' : 'VALOR'}</PriceLabel><Price>{formattedPrice}</Price><PropertyReference>Ref. {propertyReference}</PropertyReference></PriceBlock>
          </PropertyHero>
          {property.operation === 'alquiler_vacacional' && hasStayDates && (
            <StayRequest>
              <span>ESTADÍA CONSULTADA</span>
              <div><FiCalendar /><strong>{formatStayDate(stayRequest.checkIn)} — {formatStayDate(stayRequest.checkOut)}</strong></div>
              <small>{stayRequest.guests} {stayRequest.guests === 1 ? 'huésped' : 'huéspedes'} · Confirmación final a cargo del anfitrión</small>
            </StayRequest>
          )}
          <EngagementProof>
            <span>INTERÉS EN ESTA PROPIEDAD</span>
            <div>
              <strong><FiHeart /> {property.engagement?.favorites || 0} personas la guardaron</strong>
              <strong><FiMessageCircle /> {property.engagement?.contacts || 0} contactos iniciados</strong>
            </div>
          </EngagementProof>

          <QuickFacts>
            {property.bedrooms > 0 && <QuickFact><strong>{property.bedrooms}</strong><span>Dormitorios</span></QuickFact>}
            {property.rooms > 0 && <QuickFact><strong>{property.rooms}</strong><span>Ambientes</span></QuickFact>}
            {property.bathrooms > 0 && <QuickFact><strong>{property.bathrooms}</strong><span>Baños</span></QuickFact>}
            {property.surfaceTotal > 0 && <QuickFact><strong>{property.surfaceTotal} m²</strong><span>Superficie total</span></QuickFact>}
            {property.surfaceCovered > 0 && <QuickFact><strong>{property.surfaceCovered} m²</strong><span>Cubiertos</span></QuickFact>}
            {formattedSquareMeter && <QuickFact><strong>{formattedSquareMeter}</strong><span>Valor por m²</span></QuickFact>}
            {property.vacation?.maximumGuests > 0 && <QuickFact><strong>{property.vacation.maximumGuests}</strong><span>Huéspedes</span></QuickFact>}
            {property.vacation?.beds > 0 && <QuickFact><strong>{property.vacation.beds}</strong><span>Camas</span></QuickFact>}
            {property.operation === 'alquiler_vacacional' && <QuickFact><strong>{property.vacation?.minimumNights || 1}</strong><span>Noches mínimas</span></QuickFact>}
          </QuickFacts>

          <BodyLayout>
            <MainColumn>
              {property.description && (
                <ContentSection>
                  <SectionKicker>LA PROPIEDAD</SectionKicker>
                  <SectionTitle>Un lugar para conocer en detalle</SectionTitle>
                  <Description>{property.description}</Description>
                </ContentSection>
              )}

              {(property.floorPlans?.length > 0 || property.virtualTourUrl) && <ContentSection>
                <SectionKicker>RECORRÉ LA PROPIEDAD</SectionKicker>
                <SectionTitle>Planos y recorrido virtual</SectionTitle>
                <PropertyMediaLinks>
                  {property.floorPlans?.map((plan, index) => plan.type === 'image' ? (
                    <a key={`${plan.url}-${index}`} href={plan.url} target="_blank" rel="noreferrer"><img src={plan.url} alt={plan.title || `Plano ${index + 1}`} /><span>{plan.title || `Plano ${index + 1}`}</span><FiMaximize2 /></a>
                  ) : (
                    <a key={`${plan.url}-${index}`} href={plan.url} target="_blank" rel="noreferrer"><b>PDF</b><span>{plan.title || `Plano ${index + 1}`}</span><FiArrowUpRight /></a>
                  ))}
                  {property.virtualTourUrl && <a className="tour" href={property.virtualTourUrl} target="_blank" rel="noreferrer"><FiMaximize2 /><span>Iniciar recorrido virtual</span><FiArrowUpRight /></a>}
                </PropertyMediaLinks>
              </ContentSection>}

              {property.isAptoCredito && (
                <CreditCard><CreditIcon><FiCheck /></CreditIcon><div><strong>Apta para crédito hipotecario</strong><span>Esta propiedad puede financiarse mediante una entidad bancaria.</span></div></CreditCard>
              )}

              {property.operation !== 'alquiler_vacacional' && <ContentSection>
                <SectionKicker>INFORMACIÓN</SectionKicker>
                <SectionTitle>Características principales</SectionTitle>
                <DetailsGrid>
                  {details.map(([label, value, icon]) => <DetailItem key={label}><DetailIcon>{icon}</DetailIcon><div><span>{label}</span><strong>{value}</strong></div></DetailItem>)}
                </DetailsGrid>
              </ContentSection>}

              {property.operation !== 'alquiler_vacacional' && property.marketStats?.sampleSize > 0 && (
                <MarketSection>
                  <SectionKicker>REFERENCIA DE MERCADO</SectionKicker>
                  <SectionTitle>Cómo se posiciona esta propiedad</SectionTitle>
                  <MarketGrid>
                    <MarketItem><span>Promedio de la zona</span><strong>{formatMarketValue(property.marketStats.averagePricePerSquareMeter)}</strong><small>por m²</small></MarketItem>
                    <MarketItem><span>Esta propiedad</span><strong>{formattedSquareMeter || 'Sin superficie'}</strong><small>valor publicado por m²</small></MarketItem>
                  </MarketGrid>
                  <MarketSource>
                    {property.marketStats.isDemoEstimate
                      ? 'Referencia demostrativa calculada con el catálogo demo de NIDO.'
                      : `Calculado con ${property.marketStats.sampleSize} publicaciones activas de ${humanize(property.marketStats.scope)}.`}
                    {' '}Actualizado al {new Date(property.marketStats.calculatedAt || Date.now()).toLocaleDateString('es-AR')}.
                  </MarketSource>
                </MarketSection>
              )}

              {property.operation !== 'alquiler_vacacional' && featureGroups.all.length > 0 && (
                <ContentSection>
                  <SectionKicker>COMODIDADES</SectionKicker>
                  <SectionTitle>Todo lo que ofrece</SectionTitle>
                  <AmenitiesGrid>{featureGroups.all.map((feature) => (
                    <AmenityCard key={feature}>
                      <AmenityIcon>{featureIcon(feature)}</AmenityIcon>
                      <strong>{featureLabel(feature)}</strong>
                    </AmenityCard>
                  ))}</AmenitiesGrid>
                </ContentSection>
              )}

              {property.operation === 'alquiler_vacacional' && featureGroups.accommodation.length > 0 && (
                <ContentSection>
                  <SectionKicker>EL ALOJAMIENTO</SectionKicker>
                  <SectionTitle>Todo lo que ofrece</SectionTitle>
                  {featureGroups.special.length > 0 && <><GroupQuestion>¿Qué hace especial a este lugar?</GroupQuestion><SpecialFeatureGrid>{featureGroups.special.map((feature) => <SpecialFeature key={feature}><i>{featureIcon(feature)}</i><strong>{featureLabel(feature)}</strong></SpecialFeature>)}</SpecialFeatureGrid></>}
                  <GroupQuestion>¿Qué incluye para sentirte en casa?</GroupQuestion>
                  <ServiceGrid>{featureGroups.accommodation.map((feature) => <ServiceCard key={feature}><i>{featureIcon(feature)}</i><div><strong>{featureLabel(feature)}</strong><span>Disponible durante tu estadía</span></div></ServiceCard>)}</ServiceGrid>
                </ContentSection>
              )}

              {property.operation === 'alquiler_vacacional' && featureGroups.special.length > 0 && featureGroups.accommodation.length === 0 && (
                <ContentSection>
                  <SectionKicker>EL ALOJAMIENTO</SectionKicker>
                  <SectionTitle>Lo especial de este lugar</SectionTitle>
                  <GroupQuestion>¿Qué hace especial a este lugar?</GroupQuestion>
                  <SpecialFeatureGrid>{featureGroups.special.map((feature) => <SpecialFeature key={feature}><i>{featureIcon(feature)}</i><strong>{featureLabel(feature)}</strong></SpecialFeature>)}</SpecialFeatureGrid>
                </ContentSection>
              )}

              {property.operation === 'alquiler_vacacional' && featureGroups.experiences.length > 0 && (
                <ContentSection>
                  <SectionKicker>LA EXPERIENCIA</SectionKicker>
                  <SectionTitle>Una estadía con su propio ritmo</SectionTitle>
                  <FeatureGrid>{featureGroups.experiences.map((feature) => <Feature key={feature}><FiCheck />{featureLabel(feature)}</Feature>)}</FeatureGrid>
                </ContentSection>
              )}

              {property.operation === 'alquiler_vacacional' && property.location?.lat && property.location?.lng && (
                <Suspense fallback={null}><VacationNearbyHighlights location={{ lat:Number(property.location.lat), lng:Number(property.location.lng) }} interests={featureGroups.nearby} note={property.vacation?.nearbyNotes} /></Suspense>
              )}

              {property.vacation?.houseRules && <ContentSection>
                <SectionKicker>TU ESTADÍA</SectionKicker>
                <SectionTitle>Antes de llegar</SectionTitle>
                <Description><strong>Reglas de la casa</strong><br />{property.vacation.houseRules}</Description>
              </ContentSection>}
              {property.operation === 'alquiler_vacacional' && (
                <ContentSection>
                  <SectionKicker>DISPONIBILIDAD</SectionKicker>
                  <SectionTitle>Planificá tu estadía</SectionTitle>
                  <Description>
                    Check-in desde las {property.vacation?.checkIn || '15:00'} · Check-out hasta las {property.vacation?.checkOut || '10:00'} · Mínimo {property.vacation?.minimumNights || 1} {(property.vacation?.minimumNights || 1) === 1 ? 'noche' : 'noches'}.
                    {property.vacation?.petsAllowed && ' Se aceptan mascotas.'}
                  </Description>
                </ContentSection>
              )}

              {property.operation === 'alquiler_vacacional' && <ReviewsSection type="property" id={property._id} title="Lo que vivieron otros huéspedes." />}

              {(property.rental?.depositMonths > 0 || property.rental?.guarantees?.length > 0 || property.rental?.requirements) && <ContentSection>
                <SectionKicker>CONDICIONES DEL ALQUILER</SectionKicker><SectionTitle>Todo claro desde el comienzo</SectionTitle>
                <FeatureGrid>
                  {property.rental?.depositMonths > 0 && <Feature><FiCopy />Depósito: {property.rental.depositMonths} {property.rental.depositMonths === 1 ? 'mes' : 'meses'}</Feature>}
                  {property.rental?.guarantees?.map(item=><Feature key={item}><FiCheck />{humanize(item)}</Feature>)}
                  {property.rental?.guarantees?.includes('garante') && property.rental?.guarantorsCount > 0 && <Feature><FiCheck />{property.rental.guarantorsCount} {property.rental.guarantorsCount === 1 ? 'garante requerido' : 'garantes requeridos'}</Feature>}
                </FeatureGrid>
                {property.rental.requirements && <Description>{property.rental.requirements}</Description>}
              </ContentSection>}

              {(property.location?.lat && property.location?.lng) && (
                <DeferredRender fallback={<LocationSkeleton><span>UBICACIÓN Y ENTORNO</span><strong>Preparando el mapa cuando llegues a esta sección…</strong></LocationSkeleton>}>
                <ContentSection>
                  <SectionKicker>UBICACIÓN</SectionKicker>
                  <SectionTitle>Conocé el entorno</SectionTitle>
                  <LocationLead><FiMapPin />{property.address || locationText}</LocationLead>
                  <Suspense fallback={<MapLoading>Preparando mapa…</MapLoading>}><DetailMap><PropertyMap properties={[property]} /></DetailMap>
                  {streetViewAvailable !== false && <StreetViewSection>
                    <StreetViewToggle type="button" $open={streetViewOpen} onClick={() => setStreetViewOpen((value) => !value)} aria-expanded={streetViewOpen}>
                      <span><small>STREET VIEW</small><strong>Recorré la cuadra</strong></span>
                      <i>{streetViewOpen ? 'Ocultar' : 'Ver recorrido'} <FiChevronDown /></i>
                    </StreetViewToggle>
                    <StreetViewFrame $open={streetViewOpen}><GoogleStreetView location={{lat:Number(property.location.lat),lng:Number(property.location.lng)}} onAvailabilityChange={setStreetViewAvailable}/></StreetViewFrame>
                  </StreetViewSection>}
                  <NearbyPlaces location={{lat:Number(property.location.lat),lng:Number(property.location.lng)}}/>
                  </Suspense>
                </ContentSection>
                </DeferredRender>
              )}

              {property.neighborhood && (
                <NeighborhoodSection>
                  <SectionKicker>EL BARRIO</SectionKicker>
                  <BarrioInfo barrio={{ name: property.neighborhood }} department={property.department} province={property.province} />
                </NeighborhoodSection>
              )}
            </MainColumn>

            <Sidebar>
              <ContactCard>
                <ContactKicker>{property.operation === 'alquiler_vacacional' ? '¿QUERÉS QUEDARTE ACÁ?' : '¿TE INTERESA ESTA PROPIEDAD?'}</ContactKicker>
                <ContactTitle>{property.operation === 'alquiler_vacacional' ? 'Consultá disponibilidad para tus fechas.' : property.operation === 'alquiler_temporal' ? 'Consultá condiciones y disponibilidad para tu estadía.' : 'Coordiná una visita o hacé tu consulta.'}</ContactTitle>
                <Publisher
                  as={property.author?._id ? 'button' : 'div'}
                  type={property.author?._id ? 'button' : undefined}
                  $clickable={Boolean(property.author?._id)}
                  onClick={property.author?._id ? () => navigate(`/perfil/${property.author._id}`) : undefined}
                  aria-label={property.author?._id ? `Ver perfil de ${property.author?.name || 'anunciante'}` : undefined}
                >
                  <Avatar>{property.author?.avatar ? <img src={property.author.avatar} alt="" /> : <FiUser />}</Avatar>
                  <div><strong>{property.author?.name || 'Anunciante'} {property.author?.lastName || ''}</strong><span>{property.author?.role === 'PROFESSIONAL' ? 'Profesional inmobiliario' : 'Propietario particular'}</span>{property.author?.role !== 'PROFESSIONAL' && <PublisherBadge>PARTICULAR</PublisherBadge>}</div>
                  {property.author?._id && <FiArrowUpRight aria-hidden="true" />}
                </Publisher>
                {!unavailable && <PrimaryButton type="button" onClick={() => { if (!previewMode) handleContact(false); }}><FiMessageCircle /> {property.operation === 'alquiler_vacacional' ? 'Consultar disponibilidad' : 'Contactar'}</PrimaryButton>}
                {!unavailable && property.operation !== 'alquiler_vacacional' && <SecondaryButton type="button" onClick={() => { if (!previewMode) handleContact(true); }}>Coordinar una visita <FiArrowUpRight /></SecondaryButton>}
                {!unavailable && (canUseWhatsapp || property.isDemo) && <WhatsAppButton type="button" onClick={() => { if (!previewMode) handleWhatsApp(); }}>Continuar por WhatsApp</WhatsAppButton>}
                {property.operation === 'alquiler_vacacional' && (
                  <ProtectedContact>
                    NIDO facilita el contacto. La disponibilidad, contratación y pago se acuerdan directamente con el anunciante.
                  </ProtectedContact>
                )}
                <ContactNote>
                  {property.operation === 'alquiler_vacacional'
                    ? 'Esta consulta no constituye una reserva ni genera cargos. Verificá identidad, condiciones y habilitación antes de transferir dinero.'
                    : 'Al contactar aceptás iniciar una conversación con el anunciante. Nido nunca solicitará pagos por adelantado.'}
                </ContactNote>
                {isOwner && <OwnerButton type="button" onClick={() => navigate('/mis-publicaciones')}>Administrar esta publicación</OwnerButton>}
                {!isOwner && <ReportButton type="button" onClick={openReport}><FiAlertTriangle /> Denunciar esta publicación</ReportButton>}
              </ContactCard>
            </Sidebar>
          </BodyLayout>
        </Content>
        {!previewMode && !unavailable && <MobileContactBar>
          <MobilePrice><span>{property.operation === 'alquiler_vacacional' ? 'POR NOCHE' : 'VALOR'}</span><strong>{formattedPrice}</strong></MobilePrice>
          <MobileContactButton type="button" onClick={() => handleContact(false)}><FiMessageCircle /> {property.operation === 'alquiler_vacacional' ? 'Consultar disponibilidad' : 'Contactar'}</MobileContactButton>
        </MobileContactBar>}
        {notice && <Notice><FiCheck />{notice}</Notice>}
        {demoContactOpen && (
          <ReportBackdrop onMouseDown={(event) => event.target === event.currentTarget && setDemoContactOpen(false)}>
            <DemoDialog role="dialog" aria-modal="true" aria-labelledby="demo-contact-title">
              <ReportClose type="button" onClick={() => setDemoContactOpen(false)} aria-label="Cerrar"><FiX /></ReportClose>
              <DemoIcon><FiHome /></DemoIcon>
              <SectionKicker>EXPERIENCIA NIDO</SectionKicker>
              <ReportTitle id="demo-contact-title">Esta propiedad es una demo.</ReportTitle>
              <ReportIntro>La ficha fue creada para que puedas conocer cómo funciona NIDO. No corresponde a una propiedad actualmente disponible y no se enviará ninguna consulta ni mensaje de WhatsApp.</ReportIntro>
              <DemoHint>En las publicaciones reales, estos botones te conectarán directamente con el anunciante.</DemoHint>
              <DemoActions>
                <ReportCancel type="button" onClick={() => setDemoContactOpen(false)}>Seguir recorriendo</ReportCancel>
                <DemoExplore type="button" onClick={() => navigate('/properties')}>Ir al buscador <FiArrowRight /></DemoExplore>
              </DemoActions>
            </DemoDialog>
          </ReportBackdrop>
        )}
        {reportOpen && (
          <ReportBackdrop onMouseDown={(event) => event.target === event.currentTarget && setReportOpen(false)}>
            <ReportDialog role="dialog" aria-modal="true" aria-labelledby="report-title">
              <ReportClose type="button" onClick={() => setReportOpen(false)} aria-label="Cerrar"><FiX /></ReportClose>
              <SectionKicker>SEGURIDAD NIDO</SectionKicker>
              <ReportTitle id="report-title">Denunciar publicación</ReportTitle>
              <ReportIntro>Tu identidad no se compartirá con el anunciante. Revisaremos la publicación antes de tomar medidas.</ReportIntro>
              <ReportForm onSubmit={submitReport}>
                <label htmlFor="report-reason">¿Qué sucede?</label>
                <select id="report-reason" value={reportReason} onChange={(event) => setReportReason(event.target.value)}>
                  <option value="FRAUD">Posible fraude o estafa</option>
                  <option value="FALSE_INFORMATION">Información falsa o engañosa</option>
                  <option value="IMPERSONATION">Suplantación de identidad</option>
                  <option value="PROHIBITED_CONTENT">Contenido prohibido</option>
                  <option value="OTHER">Otro motivo</option>
                </select>
                <label htmlFor="report-details">Contanos un poco más <span>(opcional)</span></label>
                <textarea id="report-details" maxLength="1000" rows="5" value={reportDetails} onChange={(event) => setReportDetails(event.target.value)} placeholder="No incluyas contraseñas ni datos bancarios." />
                <ReportDeclaration>
                  Declaro que la información proporcionada es verdadera y que estoy convencido/a de que esta denuncia es necesaria para proteger a otros usuarios.
                </ReportDeclaration>
                <ReportCounter>{reportDetails.length}/1000</ReportCounter>
                {reportError && <ReportError role="alert">{reportError}</ReportError>}
                <ReportActions>
                  <ReportCancel type="button" onClick={() => setReportOpen(false)}>Cancelar</ReportCancel>
                  <ReportSubmit type="submit" disabled={reportSubmitting}>{reportSubmitting ? 'Enviando…' : 'Enviar denuncia'}</ReportSubmit>
                </ReportActions>
              </ReportForm>
            </ReportDialog>
          </ReportBackdrop>
        )}
      </DetailPanel>
    </DetailShell>
    </>
  );
}

export default PropertyDetail;

const PageState = styled.div`
  position: fixed; inset: 0; z-index: 1100; display: grid; place-content: center; justify-items: center; gap: 18px; color: #29474f; background: #f7f5ef;
`;
const Loader = styled.div`
  width: 42px; height: 42px; border: 3px solid #d9ded9; border-top-color: #b45f40; border-radius: 50%; animation: spin .8s linear infinite;
  @keyframes spin { to { transform: rotate(360deg); } }
`;
const StateButton = styled.button`
  min-height: 44px; padding: 0 20px; color: #fff; background: #29474f; border: 0; border-radius: 999px; cursor: pointer;
`;
const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 1000; display: flex; justify-content: center; padding: 18px; background: rgba(23,38,42,.44); backdrop-filter: blur(12px);
  @media (max-width: 800px) { padding: 0; }
`;
const Panel = styled.main`
  position: relative; width: min(1540px,100%); height: calc(100dvh - 36px); overflow-y: auto; color: #29474f; background: #f8f6f1;
  border-radius: 20px; box-shadow: 0 36px 100px rgba(20,35,39,.28); scrollbar-width: thin;
  @media (max-width: 800px) { height: 100dvh; border-radius: 0; }
`;
const PreviewShell = styled.div`display:block;width:100%;background:#f8f6f1;`;
const PreviewPanel = styled.main`width:100%;color:#29474f;background:#f8f6f1;.preview-content{padding-bottom:24px;}@media(max-width:980px){.preview-content{padding-bottom:24px;}}`;
const AvailabilityBanner=styled.div`display:flex;align-items:center;justify-content:space-between;gap:20px;padding:17px clamp(16px,3vw,38px);color:#29474f;background:#fff0e9;border-bottom:1px solid #efd7cc;strong,span{display:block}strong{font-size:17px}span{margin-top:4px;color:#657679;font-size:13px;line-height:1.45}a{flex:0 0 auto;display:inline-flex;min-height:44px;align-items:center;gap:7px;padding:0 17px;border-radius:999px;color:#fff;background:#b75f40;font-size:13px;font-weight:800;text-decoration:none}@media(max-width:650px){align-items:stretch;flex-direction:column;padding:15px 16px;a{justify-content:center;width:100%}}`;
const TopBar = styled.header`
  position: sticky; top: 0; z-index: 40; display: grid; grid-template-columns: 1fr auto 1fr; min-height: 72px; align-items: center; padding: 0 clamp(16px,2.5vw,36px);
  background: rgba(250,248,243,.92); border-bottom: 1px solid rgba(42,64,70,.1); backdrop-filter: blur(16px);
  @media (max-width: 700px) { grid-template-columns: 1fr auto; min-height: 64px; }
`;
const BackButton = styled.button`
  display: flex; width: fit-content; align-items: center; gap: 7px; padding: 0; color: #29474f; background: transparent; border: 0; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer;
`;
const TopIdentity = styled.div`
  display: flex; align-items: center; gap: 9px; color: #718084; font-size:12px; text-transform: capitalize;
  span { color: #29474f; font-weight: 800; letter-spacing: .14em; } i { width: 3px; height: 3px; background: #b45f40; border-radius: 50%; }
  @media (max-width: 700px) { display: none; }
`;
const TopActions = styled.div`
  display: flex; justify-content: flex-end; align-items: center; gap: 7px; flex-wrap: wrap;
`;
const CircleButton = styled.button`
  display: grid; width: 40px; height: 40px; place-items: center; color: ${({$active}) => $active ? '#fff' : '#29474f'}; background: ${({$active}) => $active ? '#b45f40' : '#fff'};
  border: 1px solid rgba(42,64,70,.12); border-radius: 50%; cursor: pointer; transition: .2s;
  &:hover { transform: translateY(-2px); border-color: #b45f40; }
`;
const TitleActions = styled.div`
  display: flex; align-items: center; margin: 10px 0 12px;
`;
const HideInlineButton = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 7px; min-height: 32px; padding: 0 12px; color: #5f6d72; background: #f4f1ee; border: 1px solid rgba(42,64,70,.08); border-radius: 999px; font: inherit; font-size: 12px; font-weight: 700; cursor: pointer; transition: background .2s ease, border-color .2s ease, color .2s ease;
  svg { font-size: 13px; }
  &:hover { color: #b45f40; background: #fff; border-color: rgba(180,95,64,.2); }
`;
const CloseButton = styled(CircleButton)`@media (min-width: 801px) { margin-left: 5px; }`;
const Gallery = styled.section`
  position: relative; height: clamp(390px,61vh,720px); overflow: hidden; background: #172b30;
  @media (max-width: 700px) { height: min(410px, 54dvh); min-height: 330px; }
`;
const DesktopGalleryMedia = styled.div`
  width:100%;height:100%;
  @media(max-width:700px){display:none;}
`;
const MobileGalleryTrack = styled.div`
  display:none;
  @media(max-width:700px){
    display:flex;width:100%;height:100%;overflow-x:auto;overflow-y:hidden;
    scroll-snap-type:x mandatory;scroll-behavior:smooth;overscroll-behavior-x:contain;
    -webkit-overflow-scrolling:touch;scrollbar-width:none;touch-action:pan-x;
    &::-webkit-scrollbar{display:none;}
  }
`;
const MobileGallerySlide = styled.div`
  position:relative;flex:0 0 100%;width:100%;height:100%;scroll-snap-align:start;scroll-snap-stop:always;
`;
const MainImage = styled.img`
  width: 100%; height: 100%; object-fit: cover;
`;
const GalleryVideo = styled.video`
  display:block;width:100%;height:100%;object-fit:contain;background:#172b30;
  @media(max-width:700px){object-fit:contain;background:radial-gradient(circle at center,#294147 0%,#172b30 72%);}
`;
const GalleryOverlay = styled.div`
  position: absolute; inset: 0; pointer-events: none; background: linear-gradient(180deg, rgba(17,29,32,.12), transparent 50%, rgba(17,29,32,.36));
`;
const GalleryArrow = styled.button`
  position: absolute; top: 50%; ${({$left}) => $left ? 'left: 22px' : 'right: 22px'}; display: grid; width: 48px; height: 48px; place-items: center;
  color: #29474f; background: rgba(255,255,255,.9); border: 0; border-radius: 50%; transform: translateY(-50%); backdrop-filter: blur(10px); cursor: pointer;
  @media (max-width: 700px) { display:none; }
`;
const GalleryMeta = styled.div`
  position: absolute; right: 24px; bottom: 22px; display: flex; align-items: center; gap: 8px;
  @media(max-width:700px){left:12px;right:12px;bottom:12px;align-items:flex-end;justify-content:flex-end;gap:5px;flex-wrap:wrap;}
`;
const PhotoCount = styled.span`
  padding: 9px 14px; color: #fff; background: rgba(30,48,52,.72); border: 1px solid rgba(255,255,255,.25); border-radius: 999px; font-size:12px; backdrop-filter: blur(10px);
`;
const Featured = styled(PhotoCount)`background: rgba(180,95,64,.92);font-weight:750;letter-spacing:.1em;@media(max-width:700px){max-width:70%;padding:6px 8px;overflow:hidden;font-size:12px;letter-spacing:.07em;white-space:nowrap;text-overflow:ellipsis;}`;
const EngagementProof = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin: 0 0 24px;
  padding: 16px 18px;
  border: 1px solid rgba(42,64,70,.12);
  border-radius: 14px;
  background: #f4f2ec;

  > span {
    color: #b45f40;
    font-size:12px;
    font-weight: 750;
    letter-spacing: .14em;
  }

  div {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 20px;
  }

  strong {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    color: #53676b;
    font-size: 13px;
  }

  @media(max-width: 650px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;
const StayRequest = styled.div`
  display:grid;gap:8px;margin:0 clamp(20px,4vw,64px) 22px;padding:18px 20px;color:#29474f;
  background:#f3eee5;border:1px solid rgba(180,95,64,.18);border-radius:16px;
  >span{color:#b45f40;font-size:12px;font-weight:760;letter-spacing:.16em}
  >div{display:flex;align-items:center;gap:9px}strong{font-size:15px;font-weight:680}svg{color:#b45f40}
  small{color:#718083;font-size:12px}
`;
const Thumbnails = styled.div`
  position: absolute; left: 24px; bottom: 20px; display: flex; gap: 7px; max-width: calc(100% - 260px); overflow: hidden;
  @media (max-width: 800px) { display: none; }
`;
const Thumbnail = styled.button`
  position:relative;width:68px;height:48px;flex:0 0 auto;overflow:hidden;padding:0;border:2px solid ${({$active}) => $active ? '#fff' : 'transparent'};border-radius:7px;opacity:${({$active}) => $active ? 1 : .7};cursor:pointer;
  img { width: 100%; height: 100%; object-fit: cover; }
  span{position:absolute;inset:auto 3px 3px;padding:2px;color:#fff;background:rgba(32,55,60,.85);border-radius:3px;font-size:12px;font-weight:850;letter-spacing:.08em;}
`;
const Content = styled.div`
  max-width: 1320px; margin: 0 auto; padding: 0 clamp(20px,4vw,62px) 100px;
  @media (max-width: 980px) { padding-bottom: 145px; }
`;
const PropertyHero = styled.section`
  display: grid; grid-template-columns: minmax(0,1fr) auto; gap: 42px; padding: clamp(34px,5vw,68px) 0 34px; border-bottom: 1px solid rgba(42,64,70,.12);
  @media (max-width: 760px) { grid-template-columns: 1fr; gap: 26px; }
`;
const HeroMain = styled.div`min-width:0;`;
const Meta = styled.div`
  margin-bottom: 12px; color: #b45f40; font-size:12px; font-weight: 750; letter-spacing: .13em; text-transform: uppercase;
`;
const PromotionDisclosure = styled.span`
  display:inline-block;margin-left:10px;padding-left:10px;border-left:1px solid rgba(180,95,64,.35);
  color:#778487;font-weight:650;letter-spacing:.03em;text-transform:none;
`;
const Title = styled.h1`
  max-width: 820px; margin: 0 0 17px; color: #20373c; font-size: clamp(34px,4.2vw,66px); font-weight: 520; line-height: 1.04; letter-spacing: -.055em; overflow-wrap: anywhere;
`;
const Address = styled.div`
  display: flex; min-width: 0; align-items: flex-start; gap: 8px; color: #728084; font-size: 14px; line-height: 1.5; overflow-wrap: anywhere; svg { flex: 0 0 auto; margin-top: 3px; color: #b45f40; }
`;
const PriceBlock = styled.div`
  align-self: end; min-width: 230px; padding-left: 34px; border-left: 1px solid rgba(42,64,70,.13);
  @media (max-width: 760px) { padding: 0; border: 0; }
`;
const PropertyReference = styled.span`display:inline-block;margin-top:9px;color:#7d898b;font-size:12px;font-weight:650;letter-spacing:.1em;text-transform:uppercase;`;
const PriceLabel = styled.div`margin-bottom: 7px; color: #819092; font-size:12px; font-weight: 750; letter-spacing: .15em;`;
const Price = styled.div`color: #20373c; font-size: clamp(29px,3vw,47px); font-weight: 680; letter-spacing: -.05em; overflow-wrap: anywhere;`;
const QuickFacts = styled.div`
  display: flex; overflow-x: auto; border-bottom: 1px solid rgba(42,64,70,.12); scrollbar-width: none;
`;
const QuickFact = styled.div`
  min-width: 145px; padding: 25px clamp(20px,3vw,42px) 25px 0; margin-right: clamp(20px,3vw,42px); border-right: 1px solid rgba(42,64,70,.1);
  strong,span { display: block; } strong { margin-bottom: 4px; color: #29474f; font-size: 20px; } span { color: #819092; font-size:12px; }
`;
const BodyLayout = styled.div`
  display: grid; width: 100%; min-width: 0; grid-template-columns: minmax(0,1.58fr) minmax(360px,.9fr); align-items: start; gap: clamp(42px,6vw,86px); padding-top: clamp(50px,7vw,88px);
  @media (max-width: 980px) { grid-template-columns: 1fr; }
`;
const MainColumn = styled.div`display: flex; min-width: 0; flex-direction: column; gap: clamp(62px,8vw,100px);`;
const ContentSection = styled.section`width:100%;min-width:0;`;
const MarketSection = styled(ContentSection)`padding:clamp(24px,3vw,38px);background:#edf1ed;border:1px solid rgba(42,64,70,.12);border-radius:18px;`;
const MarketGrid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));border-top:1px solid rgba(42,64,70,.13);@media(max-width:650px){grid-template-columns:1fr;}`;
const MarketItem = styled.div`padding:22px 20px 8px 0;span,strong,small{display:block}span{color:#6f8083;font-size:12px}strong{margin:8px 0 3px;color:#20373c;font-size:clamp(21px,2vw,29px)}small{color:#7e8c8e;font-size:12px}`;
const MarketSource = styled.p`margin:18px 0 0;color:#6f8083;font-size:12px;line-height:1.55;`;
const SectionKicker = styled.div`margin-bottom:10px;color:#b45f40;font-size:12px;font-weight:750;letter-spacing:.16em;@media(max-width:700px){font-size:12px;}`;
const SectionTitle = styled.h2`margin: 0 0 25px; color: #20373c; font-size: clamp(25px,2.5vw,38px); font-weight: 540; letter-spacing: -.04em;`;
const PropertyMediaLinks = styled.div`display:flex;flex-wrap:wrap;gap:12px;a{display:grid;grid-template-columns:64px minmax(0,1fr) auto;align-items:center;gap:12px;width:min(100%,360px);min-height:76px;padding:8px;color:#29474f;background:#fff;border:1px solid #dce4e1;border-radius:14px;text-decoration:none;transition:.2s;&:hover{border-color:#c66f4e;transform:translateY(-2px);}img,b{width:64px;height:58px;border-radius:9px;object-fit:cover;}b{display:grid;place-items:center;background:#f6e6de;color:#aa593c;font-size:13px;}span{min-width:0;font-size:14px;font-weight:720;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}svg{color:#b45f40;}.tour{grid-template-columns:auto minmax(0,1fr) auto;padding:0 18px;background:#29474f;color:#fff;svg{color:#f0ad8d;}}@media(max-width:560px){a{width:100%;}}`;
const Description = styled.p`
  width:100%;max-width:760px;margin:0;color:#526267;font-size:clamp(16px,1.35vw,19px);line-height:1.85;
  white-space:pre-wrap;overflow-wrap:anywhere;word-break:break-word;hyphens:auto;
`;
const CreditCard = styled.div`
  display: flex; align-items: center; gap: 17px; padding: 22px; background: #e8efeb; border: 1px solid rgba(54,98,81,.14); border-radius: 14px;
  strong,span { display: block; } strong { margin-bottom: 4px; color: #29474f; } span { color: #64746f; font-size: 13px; }
`;
const CreditIcon = styled.div`display: grid; width: 42px; height: 42px; flex: 0 0 auto; place-items: center; color: #fff; background: #497461; border-radius: 50%;`;
const DetailsGrid = styled.div`
  display: grid; grid-template-columns: repeat(3,minmax(0,1fr)); border-top: 1px solid rgba(42,64,70,.13); border-left: 1px solid rgba(42,64,70,.13);
  @media (max-width: 700px) { grid-template-columns: repeat(2,1fr); }
`;
const DetailItem = styled.div`
  display: flex; min-height: 106px; align-items: center; gap: 13px; padding: 18px; border-right: 1px solid rgba(42,64,70,.13); border-bottom: 1px solid rgba(42,64,70,.13);
  span,strong { display: block; } span { margin-bottom: 4px; color: #819092; font-size:12px; letter-spacing: .04em; } strong { color: #29474f; font-size: 14px; text-transform: capitalize; }
`;
const DetailIcon = styled.div`display: grid; width: 34px; height: 34px; flex: 0 0 auto; place-items: center; color: #b45f40; background: #f0e7df; border-radius: 50%;`;
const FeatureGrid = styled.div`display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 12px 30px; @media(max-width:600px){grid-template-columns:1fr;}`;
const Feature = styled.div`display: flex; align-items: center; gap: 11px; padding: 13px 0; color: #526267; border-bottom: 1px solid rgba(42,64,70,.1); font-size: 14px; svg { color: #b45f40; }`;
const AmenitiesGrid = styled.div`
  display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:11px;
  @media(max-width:700px){grid-template-columns:repeat(2,minmax(0,1fr));gap:9px;}
`;
const AmenityCard = styled.div`
  display:flex;min-height:76px;align-items:center;gap:13px;padding:15px 16px;color:#29474f;background:#f4f2ec;border:1px solid rgba(42,64,70,.09);border-radius:15px;
  strong{min-width:0;font-size:14px;font-weight:650;line-height:1.3;overflow-wrap:anywhere;}
  @media(max-width:700px){min-height:82px;align-items:flex-start;flex-direction:column;gap:9px;padding:13px;strong{font-size:13px;}}
`;
const AmenityIcon = styled.i`
  display:grid;width:38px;height:38px;flex:0 0 auto;place-items:center;color:#b45f40;background:#fff;border-radius:12px;font-style:normal;box-shadow:0 7px 18px rgba(42,64,70,.07);
  @media(max-width:700px){width:34px;height:34px;}
`;
const GroupQuestion = styled.h3`margin:22px 0 13px;color:#405a60;font-size:clamp(16px,1.4vw,19px);font-weight:650;letter-spacing:-.015em;&:first-of-type{margin-top:0}@media(max-width:700px){font-size:17px;line-height:1.35}`;
const SpecialFeatureGrid = styled.div`display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:12px;margin-bottom:16px;@media(max-width:700px){display:flex;overflow-x:auto;margin-right:-20px;padding:2px 20px 10px 0;scrollbar-width:none;scroll-snap-type:x mandatory;}`;
const SpecialFeature = styled.div`display:grid;min-height:128px;align-content:center;gap:10px;padding:19px;color:#fff;background:linear-gradient(145deg,#29474f,#3d6269);border-radius:17px;box-shadow:0 14px 34px rgba(41,71,79,.14);i{display:grid;width:35px;height:35px;place-items:center;color:#29474f;background:#f1e5da;border-radius:50%;font-style:normal}strong{font-size:17px;font-weight:680}@media(max-width:700px){width:190px;flex:0 0 190px;scroll-snap-align:start;strong{font-size:16px}}`;
const ServiceGrid = styled.div`display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:11px;@media(max-width:600px){grid-template-columns:repeat(2,minmax(0,1fr));gap:9px}`;
const ServiceCard = styled.div`display:flex;min-height:86px;align-items:center;gap:13px;padding:15px;color:#29474f;background:#f4f2ec;border:1px solid rgba(42,64,70,.09);border-radius:15px;i{display:grid;width:38px;height:38px;flex:0 0 auto;place-items:center;color:#b45f40;background:#fff;border-radius:12px;font-style:normal;box-shadow:0 7px 18px rgba(42,64,70,.07)}strong,span{display:block}strong{font-size:14px}span{margin-top:4px;color:#7b898b;font-size:12px;line-height:1.35}@media(max-width:600px){min-height:122px;align-items:flex-start;flex-direction:column;padding:14px;gap:10px;i{width:34px;height:34px}strong,span{font-size:12px}}`;
const LocationLead = styled.div`display: flex; align-items: center; gap: 8px; margin: -10px 0 18px; color: #68777b; font-size: 13px; svg { color: #b45f40; }`;
const DetailMap = styled.div`height: 440px; overflow: hidden; border: 1px solid rgba(42,64,70,.12); border-radius: 16px; box-shadow: 0 18px 45px rgba(42,64,70,.08);`;
const LocationSkeleton=styled.div`display:grid;min-height:260px;align-content:center;gap:9px;padding:28px;border-radius:18px;background:#ebece6;color:#6f7f81;span{color:#b45f40;font-size:12px;font-weight:800;letter-spacing:.16em}strong{font-size:17px}`;
const MapLoading=styled.div`display:grid;height:300px;place-items:center;border-radius:16px;background:#ebece6;color:#6f7f81;font-weight:700`;
const StreetViewSection=styled.div`margin-top:18px;overflow:hidden;border:1px solid rgba(42,64,70,.12);border-radius:18px;background:#fff;box-shadow:0 18px 45px rgba(42,64,70,.07)`;
const StreetViewToggle=styled.button`
  display:flex;width:100%;min-height:78px;align-items:center;justify-content:space-between;gap:18px;padding:17px 20px;border:0;background:#fff;color:#29474f;text-align:left;cursor:pointer;
  span small,span strong{display:block}span small{margin-bottom:5px;color:#b45f40;font-size:12px;font-weight:850;letter-spacing:.16em}span strong{font-size:18px}
  i{display:flex;align-items:center;gap:8px;font-style:normal;font-size:12px;font-weight:750}svg{transition:transform .2s;transform:${({$open})=>$open?'rotate(180deg)':'none'}}
  @media(max-width:700px){position:sticky;top:64px;z-index:2;min-height:72px;padding:14px 16px;span strong{font-size:17px}}
`;
const StreetViewFrame=styled.div`
  height:${({$open})=>$open?'420px':'0'};overflow:hidden;opacity:${({$open})=>$open?1:0};transition:height .3s ease,opacity .2s ease;
  @media(max-width:700px){height:${({$open})=>$open?'min(58vh,430px)':'0'};}
`;
const NeighborhoodSection = styled.section`
  > div { margin-top: 0; } h3 { margin: 0 0 18px; color: #20373c; font-size: clamp(25px,2.5vw,38px); font-weight: 540; letter-spacing: -.04em; }
  p { max-width: 760px; color: #526267; font-size: 16px; line-height: 1.75; }
  span { color: #526267; background: #edf1ed; border-radius: 999px; }
  img { border-radius: 12px !important; }
`;
const Sidebar = styled.aside`position:sticky;top:104px;min-width:0;@media (max-width:980px){position:static;width:100%;}`;
const ContactCard = styled.div`
  max-width: 470px; margin-left: auto; padding: clamp(25px,2.4vw,34px); background: #fff; border: 1px solid rgba(42,64,70,.12); border-radius: 18px; box-shadow: 0 24px 60px rgba(42,64,70,.1);
  @media (max-width: 980px) { max-width: none; margin-left: 0; }
`;
const ContactKicker = styled.div`margin-bottom: 10px; color: #b45f40; font-size:12px; font-weight: 750; letter-spacing: .15em;`;
const ContactTitle = styled.h3`margin: 0 0 25px; color: #20373c; font-size: clamp(22px,1.9vw,30px); font-weight: 540; line-height: 1.16; letter-spacing: -.03em; text-wrap: balance;`;
const Publisher = styled.div`
  display: flex; width:100%; align-items: center; gap: 12px; margin-bottom: 24px; padding: 17px 0; color:inherit; text-align:left; border:0; border-top: 1px solid rgba(42,64,70,.1); border-bottom: 1px solid rgba(42,64,70,.1); background:transparent;
  > div:nth-child(2){min-width:0;flex:1;} > svg{flex:0 0 auto;color:#b45f40;font-size:18px;transition:transform .18s ease;}
  strong,span { display: block; } strong { margin-bottom: 4px; color: #29474f; font-size: 13px; } span { color: #819092; font-size:12px; }
  ${({ $clickable }) => $clickable && `cursor:pointer;&:hover strong{color:#b45f40;}&:hover > svg{transform:translate(2px,-2px);}&:focus-visible{outline:2px solid #c66f4e;outline-offset:4px;border-radius:8px;}`}
`;
const PublisherBadge = styled.b`display:inline-flex !important;margin-top:7px;padding:4px 7px;color:#b45f40;background:#f5e9e2;border-radius:999px;font-size:9px !important;font-weight:800 !important;letter-spacing:.12em;`;
const Avatar = styled.div`display: grid; width: 46px; height: 46px; flex: 0 0 auto; overflow: hidden; place-items: center; color: #fff; background: #6b8083; border-radius: 50%; img { width:100%;height:100%;object-fit:cover; }`;
const ContactButton = styled.button`
  display: flex; width: min(100%, 348px); min-height: 48px; align-items: center; justify-content: center; gap: 8px; margin: 9px auto 0; padding: 0 20px; border-radius: 999px; font: inherit; font-size: 13px; font-weight: 700; cursor: pointer;
  @media (max-width: 980px) { width: min(100%, 290px); }
`;
const PrimaryButton = styled(ContactButton)`color: #fff; background: #b45f40; border: 1px solid #b45f40; &:hover { background:#9f4f34; }`;
const SecondaryButton = styled(ContactButton)`color: #29474f; background: #eef1ee; border: 1px solid #eef1ee;`;
const WhatsAppButton = styled(ContactButton)`color: #315c49; background: #e5f2e9; border: 1px solid #cfe6d6;`;
const ProtectedContact = styled.p`margin:14px 0 0;padding:12px 14px;color:#52686d;background:#eef3f1;border-radius:12px;font-size:12px;line-height:1.5;text-align:center;`;
const ContactNote = styled.p`margin: 25px 0 0; color: #929b9a; font-size:12px; line-height: 1.55; text-align: center;`;
const OwnerButton = styled(ContactButton)`color:#29474f;background:transparent;border:1px solid rgba(42,64,70,.16);`;
const ReportButton = styled.button`
  display:flex;align-items:center;justify-content:center;gap:7px;margin:18px auto 0;padding:3px 0;color:#798688;background:transparent;border:0;border-bottom:1px solid transparent;font:inherit;font-size:12px;cursor:pointer;
  &:hover{color:#a34e36;border-bottom-color:currentColor;}
`;
const ReportBackdrop = styled.div`
  position:fixed;inset:0;z-index:120;display:grid;place-items:center;padding:20px;background:rgba(23,38,42,.62);backdrop-filter:blur(8px);
`;
const ReportDialog = styled.div`
  position:relative;width:min(520px,100%);max-height:calc(100dvh - 40px);overflow-y:auto;padding:clamp(26px,5vw,42px);color:#29474f;background:#fbfaf7;border-radius:20px;box-shadow:0 30px 90px rgba(13,28,32,.35);
`;
const DemoDialog = styled(ReportDialog)`text-align:center;${SectionKicker}{margin-top:14px}`;
const DemoIcon = styled.div`display:grid;width:58px;height:58px;margin:0 auto 18px;place-items:center;color:#fff;background:#29474f;border-radius:50%;font-size:23px;`;
const DemoHint = styled.p`margin:20px 0 0;padding:14px 16px;color:#52686d;background:#eef3f1;border-radius:13px;font-size:13px;line-height:1.55;`;
const DemoActions = styled.div`display:flex;justify-content:center;gap:9px;margin-top:24px;@media(max-width:520px){flex-direction:column-reverse;button{width:100%}}`;
const DemoExplore = styled.button`display:flex;min-height:44px;align-items:center;justify-content:center;gap:8px;padding:0 20px;color:#fff;background:#b45f40;border:0;border-radius:999px;font-weight:750;cursor:pointer;`;
const ReportClose = styled.button`
  position:absolute;top:17px;right:17px;display:grid;width:38px;height:38px;place-items:center;color:#526267;background:#f0eee8;border:0;border-radius:50%;cursor:pointer;
`;
const ReportTitle = styled.h2`margin:0 42px 10px 0;color:#20373c;font-size:clamp(27px,4vw,36px);font-weight:540;letter-spacing:-.04em;`;
const ReportIntro = styled.p`margin:0 0 24px;color:#657579;font-size:14px;line-height:1.65;`;
const ReportForm = styled.form`
  display:grid;gap:9px;
  label{margin-top:8px;color:#29474f;font-size:12px;font-weight:750;letter-spacing:.01em;}label span{color:#899496;font-weight:500;}
  select,textarea{width:100%;box-sizing:border-box;padding:13px 14px;color:#29474f;background:#fff;border:1px solid rgba(42,64,70,.16);border-radius:12px;font:inherit;font-size:14px;outline:none;transition:all .2s ease;}
  select::placeholder,textarea::placeholder{color:#9aa3a6;opacity:1;}
  select:focus,textarea:focus{border-color:#b45f40;box-shadow:0 0 0 3px rgba(180,95,64,.12);}textarea{min-height:112px;resize:vertical;line-height:1.5;}
`;
const ReportCounter = styled.span`justify-self:end;color:#899496;font-size:12px;`;
const ReportDeclaration = styled.p`margin:-2px 0 2px;color:#7a878a;font-size:12px;line-height:1.45;`;
const ReportError = styled.p`margin:4px 0 0;padding:10px 12px;color:#8f3e2c;background:#f8e9e4;border-radius:9px;font-size:12px;`;
const ReportActions = styled.div`display:flex;justify-content:flex-end;gap:9px;margin-top:15px;`;
const ReportCancel = styled.button`min-height:44px;padding:0 18px;color:#526267;background:transparent;border:1px solid rgba(42,64,70,.16);border-radius:999px;font-weight:700;cursor:pointer;`;
const ReportSubmit = styled.button`min-height:44px;padding:0 20px;color:#fff;background:#b45f40;border:0;border-radius:999px;font-weight:750;cursor:pointer;&:disabled{opacity:.6;cursor:wait;}`;
const Notice = styled.div`
  position: fixed; right: 28px; bottom: 28px; z-index: 80; display: flex; align-items: center; gap: 8px; padding: 13px 18px; color: #fff; background: #29474f; border-radius: 999px; box-shadow: 0 16px 40px rgba(20,35,39,.25); font-size: 12px;
  @media(max-width:600px){left:16px;right:16px;bottom:92px;justify-content:center;}
`;
const MobileContactBar = styled.div`
  display: none;
  @media (max-width: 980px) {
    position: fixed; left: 0; right: 0; bottom: 0; z-index: 60; display: flex; align-items: center; justify-content: space-between; gap: 14px;
    min-height: 78px; padding: 14px 16px calc(20px + env(safe-area-inset-bottom)); background: rgba(252,251,248,.96);
    border-top: 1px solid rgba(42,64,70,.13); box-shadow: 0 -12px 34px rgba(42,64,70,.09); backdrop-filter: blur(16px);
  }
`;
const MobilePrice = styled.div`
  min-width:0;span,strong{display:block;}span{margin-bottom:3px;color:#819092;font-size:12px;font-weight:750;letter-spacing:.13em;}
  strong{max-width:46vw;overflow:hidden;color:#20373c;font-size:22px;font-weight:720;letter-spacing:-.04em;text-overflow:ellipsis;white-space:nowrap;}
`;
const MobileContactButton = styled.button`
  display:flex;min-height:48px;align-items:center;justify-content:center;gap:7px;padding:0 18px;color:#fff;background:#b45f40;border:0;border-radius:999px;font-size:13px;font-weight:750;cursor:pointer;
`;
